import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  LISTING_AGENT_PHONE_TYPES,
  listingsApi,
  type ListingAgentDto,
  type ListingAgentPhoneType,
  type ListingDetailDto,
  type UpdateListingPayload,
  type UpsertListingAgentPayload,
} from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, FormField, Input, useToast } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { ListingAgentPicker } from "@/components/buyers/ListingAgentPicker";
import { buyerSubmissionKeys, listingKeys } from "@/hooks/buyers/buyerKeys";
import {
  agentFormErrors,
  agentHasIdentity,
  agentToPayload,
  firmContactByDefault,
  ListingAgentEditModal,
  seedAgentForm,
  type AgentPastePrefill,
} from "../ListingAgentDirectory";
import { AgentPasteToFill, PasteToFillButton, type AgentPasteChoice } from "./agentPaste/AgentPasteToFill";
import { FindAgentLink } from "./agentResearch/AgentResearchEntry";

/**
 * The Overview "Listing agent" card's own door.
 *
 * <p>🔴 <b>Two records meet on this card, and the modal keeps them apart.</b> Who
 * is attached is a fact about the LISTING (<c>PATCH /listings/{id}</c>); the
 * agent's name and email are facts about a SHARED AGENT record every listing they
 * represent reads (<c>PATCH /listings/agents/{id}</c>). Flattening the two into
 * one save would let a specialist think they were correcting this listing while
 * they rewrote a record on four others.</p>
 *
 * <p><b>Exactly ONE of the two writes fires per save, and the picker decides
 * which.</b> Change the attachment and only the listing is patched — the details
 * block hides, because the fields on screen belong to the agent being detached
 * and editing THEM while swapping is the "workflow action wearing a pencil"
 * §4.1a.1 warns about. Leave the attachment alone and only the agent is patched.
 * There is deliberately no path where both fire: two sequential mutations where
 * the first succeeds and the second fails leaves a reader unable to tell what
 * landed.</p>
 *
 * <p>🔴 <b>Why the agent's own fields are editable from a listing at all.</b>
 * They were not, anywhere, until now: <c>PATCH /listings/agents/{id}</c> has
 * existed with an email-collision pre-check since the module shipped, and the
 * only UI reaching it was the directory modal on the LIST page. The capability
 * existed; the door was missing. That gap has a cost the card already names —
 * agents are get-or-created BY EMAIL, and PR B's emailless fallback probes only
 * <c>Email == null</c>, so an agent with no address is invisible to the dedupe
 * probe and the next pasted listing mints a SECOND record carrying part of the
 * listing count. Adding an email here promotes the row out of that fuzzy pool
 * permanently, which is why this modal exists on the record where the gap is
 * visible.</p>
 *
 * <p>Recruitment is NOT here. It is a workflow state that mints a broker lead,
 * and §4.1a reaches it at full strength — it stays on the page header's overflow,
 * which the record drawer deliberately does not have.</p>
 */
export function ListingAgentSectionModal({
  listing,
  open,
  onClose,
}: {
  listing: ListingDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const pickerInputRef = useRef<HTMLInputElement | null>(null);

  const savedAgentId = listing.listingAgentId ?? "";
  const agent = listing.listingAgent ?? null;

  // `touched` is what separates "the specialist chose this" from "this is what
  // the listing holds". Untouched, the picker shows the listing's agent as the
  // server last returned it, so a refetch while the modal is open never leaves
  // a stale name on screen.
  const [choice, setChoice] = useState<ListingAgentDto | null>(agent);
  const [touched, setTouched] = useState(false);
  const [form, setForm] = useState<ListingAgentSectionForm>(() => agentSectionForm(agent));
  const [error, setError] = useState<string | null>(null);
  const [creatingAgent, setCreatingAgent] = useState(false);
  // D1b — PASTE TO FILL beside + NEW AGENT: the paste box, and the paste the
  // New agent form opens from once the duplicate check has run on it.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [prefill, setPrefill] = useState<AgentPastePrefill | null>(null);

  // 🔴 Reset on OPENING, never on a change of `listing`. Creating an agent from
  // + NEW AGENT refreshes every listing query, and a reset keyed on the
  // listing's identity would throw away the agent the specialist just created
  // and selected — along with any contact-field edits they had typed.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setChoice(agent);
      setTouched(false);
      setForm(agentSectionForm(agent));
      setError(null);
      setCreatingAgent(false);
      setPasteOpen(false);
      setPrefill(null);
    }
  }

  const choose = (next: ListingAgentDto | null) => {
    setTouched(true);
    setChoice(next);
  };

  const shown = touched ? choice : agent;
  const attachmentChanged = touched && (choice?.id ?? "") !== savedAgentId;
  // Only the ATTACHED agent's own fields are on offer, and only while the
  // attachment is untouched — see the class comment. A just-created agent is a
  // changed attachment, so its fields stay hidden until SAVE attaches it.
  const editingDetails = Boolean(agent) && !attachmentChanged;
  const errors = agentFormErrors({ ...EMPTY_IDENTITY, ...form });
  const detailsDirty = editingDetails && agentSectionIsDirty(agent, form);
  const detailsValid =
    !editingDetails ||
    (!errors.email && !errors.phone && agentHasIdentity({ ...EMPTY_IDENTITY, ...form }));

  const canSave = (attachmentChanged || detailsDirty) && detailsValid;

  /*
    The pasted person goes through the SAME duplicate check + NEW AGENT's Save
    runs, on exactly the values the New agent form will open with, before the
    form opens. Matches open it on "Already on file?" (USE THIS AGENT); none
    open it filled and ready to save. It writes nothing either way — the
    form's own Save still checks again and creates strictly.
  */
  const checkPasted = useMutation({
    mutationFn: async (choice: AgentPasteChoice): Promise<AgentPastePrefill> => {
      const { form: pastedForm } = seedAgentForm(null, { choice });
      const duplicateCheck = await listingsApi.checkAgentDuplicates({
        ...agentToPayload(pastedForm, { forEdit: false }),
        isFirmContact: firmContactByDefault(pastedForm),
        rejectExisting: true,
      });
      return { choice, duplicateCheck };
    },
    // no-invalidation: the duplicate check reads and writes nothing; the New agent form's save invalidates.
    onSuccess: (next) => {
      setPrefill(next);
      setCreatingAgent(true);
    },
    // A failed check is not a reason to lose the paste: the form opens filled,
    // and its Save runs the check again.
    onError: (_e, choice) => {
      setPrefill({ choice, duplicateCheck: null });
      setCreatingAgent(true);
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (attachmentChanged) {
        const patch: UpdateListingPayload = {};
        if (choice) patch.listingAgentId = choice.id;
        // A nullable id cannot express "detach" on its own, since bare null
        // already means unchanged.
        else patch.clearListingAgent = true;
        await listingsApi.update(listing.id, patch);
        return;
      }
      if (!agent) return;
      await listingsApi.updateAgent(agent.id, agentSectionPayload(form));
    },
    onSuccess: () => {
      // The whole listing prefix rather than this record alone: an agent's name
      // and firm are denormalised onto every listing row and detail that renders
      // them, so refreshing one would leave stale names on screen for the full
      // five-minute staleTime.
      void qc.invalidateQueries({ queryKey: listingKeys.all });
      // 🔴 And every submission. A submission's broker intro email is addressed
      // to the listing's agent, and the escalation gate reads that agent's
      // contact details — both are read under the submission prefix, so a
      // changed agent or a corrected email would otherwise reach those screens
      // only after the staleTime, or a reload.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(attachmentChanged ? "Listing agent updated." : "Agent updated.");
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  return (
    <>
      <Modal
        open={open}
        onClose={() => (save.isPending ? undefined : onClose())}
        title="Edit listing agent"
        description="Who represents the seller on this listing, and how to reach them."
        size="md"
        dismissOnBackdropClick={false}
        initialFocusRef={pickerInputRef}
        footer={
          <div className="flex justify-end gap-space-2">
            <Button variant="text" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
              disabled={save.isPending || !canSave}
              data-testid="listing-agent-save"
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      >
        <div className="grid gap-space-4">
          <ListingAgentPicker
            label="Agent on this listing"
            value={shown}
            onChange={choose}
            allowNone
            onNewAgent={() => {
              setPrefill(null);
              setCreatingAgent(true);
            }}
            extraActions={
              <>
                <PasteToFillButton
                  onClick={() => setPasteOpen(true)}
                  disabled={save.isPending || checkPasted.isPending || pasteOpen}
                  data-testid="listing-agent-paste-open"
                />
                {/* D7 — FIND AGENT: a link to the research workspace, never a write. */}
                <FindAgentLink listingId={listing.id} />
              </>
            }
            disabled={save.isPending}
            inputRef={pickerInputRef}
            testIdPrefix="listing-agent-choice"
            unresolvedLabel={
              !touched && savedAgentId && !agent
                ? "The agent on this listing is no longer in the directory."
                : undefined
            }
          />

          <AgentPasteToFill
            open={pasteOpen}
            onOpenChange={setPasteOpen}
            hideTrigger
            onPick={(choice) => checkPasted.mutate(choice)}
            disabled={save.isPending || checkPasted.isPending}
            testIdPrefix="listing-agent-paste"
          />
          {checkPasted.isPending && (
            <p className="text-caption text-gray-mid m-0" role="status">
              Checking the agents already on file…
            </p>
          )}

          {attachmentChanged && (
            <p className="text-caption text-gray-mid m-0" data-testid="listing-agent-swap-note">
              {choice
                ? "Saving attaches this agent to the listing. Their own details are edited from their record, so they are not offered while the attachment is changing."
                : "Saving detaches the current agent. The agent record itself is shared and stays on file."}
            </p>
          )}

          {editingDetails && agent && (
            <div className="grid gap-space-3 border-t border-border-light pt-space-4">
              {/*
                🔴 THE SHARED-RECORD WARNING, and it is shown BEFORE the save
                rather than confirmed after it. `listingCount` is already on the
                card and on the DTO, so this costs no extra read — and a specialist
                correcting a firm name has no other way to know they are about to
                change it on three other listings they have never opened.
              */}
              <p
                className="text-caption text-gray-mid m-0"
                data-testid="listing-agent-shared-warning"
              >
                Shared record — {agent.displayName} appears on{" "}
                <strong className="text-onyx">
                  {agent.listingCount} {agent.listingCount === 1 ? "listing" : "listings"}
                </strong>
                . Changes here apply everywhere they are shown.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
                <FormField label="First name">
                  <Input
                    value={form.firstName}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "firstName", e.target.value)}
                    data-testid="listing-agent-firstName"
                  />
                </FormField>
                <FormField label="Last name">
                  <Input
                    value={form.lastName}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "lastName", e.target.value)}
                    data-testid="listing-agent-lastName"
                  />
                </FormField>
                <FormField label="Firm">
                  <Input
                    value={form.firmName}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "firmName", e.target.value)}
                    data-testid="listing-agent-firmName"
                  />
                </FormField>
                <FormField label="Title">
                  <Input
                    value={form.title}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "title", e.target.value)}
                    data-testid="listing-agent-title"
                  />
                </FormField>
                <FormField label="Email" error={errors.email}>
                  <Input
                    type="email"
                    value={form.email}
                    invalid={Boolean(errors.email)}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "email", e.target.value)}
                    data-testid="listing-agent-email"
                  />
                </FormField>
                <FormField label="Phone" error={errors.phone}>
                  <Input
                    type="tel"
                    value={form.phone}
                    invalid={Boolean(errors.phone)}
                    disabled={save.isPending}
                    onChange={(e) => setField(setForm, "phone", e.target.value)}
                    data-testid="listing-agent-phone"
                  />
                </FormField>
                <FormField label="Phone type">
                  <Select
                    value={form.phoneType}
                    disabled={save.isPending}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phoneType: e.target.value as ListingAgentPhoneType }))
                    }
                    data-testid="listing-agent-phone-type"
                  >
                    {LISTING_AGENT_PHONE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>

              {agent.isFirmContact && (
                <p className="text-caption text-gray-mid m-0" data-testid="listing-agent-firm-contact-note">
                  Firm contact (no named agent) — a firm&apos;s general mailbox or
                  main line. When a named agent is found, attach them instead; the
                  firm contact stays on file for other listings.
                </p>
              )}

              {!form.email.trim() && (
                /*
                  The defect notice from the card, restated where it can be fixed.
                  An agent with no email is invisible to the get-or-create probe, so
                  every future paste naming them mints a duplicate record — and the
                  duplicates then each carry part of the listing count, which is
                  what makes the damage hard to spot after the fact.
                */
                <p className="text-caption text-gray-mid m-0">
                  Agents are matched by email address. Without one, pasting a
                  listing that names this agent creates a second record instead of
                  finding this one.
                </p>
              )}

              <p className="text-caption text-gray-mid m-0">
                City, state, notes and broker recruitment are not edited here — they
                are not shown on this card. The listing-agent directory on the
                listings list carries the full record.
              </p>
            </div>
          )}

          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </div>
      </Modal>

      {/*
        + NEW AGENT opens the directory's own New agent form as a SIBLING modal,
        never nested inside this one: Escape closes only the form, focus returns
        to + NEW AGENT, and this modal keeps its selection and any unsaved edits.
        Creating selects the new agent here and writes nothing to the listing —
        SAVE attaches it. USE THIS AGENT selects the existing agent the same way.
        PASTE TO FILL opens the same form, filled from the paste.
      */}
      <ListingAgentEditModal
        open={open && creatingAgent}
        onClose={() => {
          setCreatingAgent(false);
          setPrefill(null);
        }}
        onSaved={choose}
        onUseExisting={(existing) => {
          choose(existing);
          setCreatingAgent(false);
          setPrefill(null);
        }}
        initialPaste={prefill}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// The five fields this card shows, and nothing else
// ---------------------------------------------------------------------------

/**
 * 🔴 SCOPED, and that is the point rather than an omission.
 *
 * <p>The directory's <c>agentToPayload(form, {forEdit: true})</c> sends EVERY
 * agent field trimmed, which is correct there because its form renders every one
 * — but over a PATCH where <c>""</c> means clear, reusing it here would wipe the
 * agent's city, state and notes, none of which this card displays and none of
 * which the specialist can see. §4.1a.1 again: an edit is licensed where the
 * value it changes is rendered beside it.</p>
 */
export interface ListingAgentSectionForm {
  firstName: string;
  lastName: string;
  firmName: string;
  title: string;
  email: string;
  phone: string;
  /** D20 — shown on the card beside the phone, so it is edited here too. */
  phoneType: ListingAgentPhoneType;
}

const EMPTY_FORM: ListingAgentSectionForm = {
  firstName: "",
  lastName: "",
  firmName: "",
  title: "",
  email: "",
  phone: "",
  phoneType: "Unknown",
};

/** The fields `agentFormErrors` / `agentHasIdentity` expect but this form does
 *  not carry, so those two shared validators can be reused unchanged. */
const EMPTY_IDENTITY = { city: "", state: "", notes: "" };

export function agentSectionForm(
  agent?: Pick<
    ListingAgentDto,
    "firstName" | "lastName" | "firmName" | "title" | "email" | "phone" | "phoneType"
  > | null,
): ListingAgentSectionForm {
  if (!agent) return EMPTY_FORM;
  return {
    firstName: agent.firstName ?? "",
    lastName: agent.lastName ?? "",
    firmName: agent.firmName ?? "",
    title: agent.title ?? "",
    email: agent.email ?? "",
    phone: agent.phone ?? "",
    phoneType: agent.phoneType ?? "Unknown",
  };
}

/** PATCH semantics: every field this form OWNS is sent, so emptying one clears
 *  it; every field it does not own is omitted, so it stays as it is. */
export function agentSectionPayload(
  form: ListingAgentSectionForm,
): UpsertListingAgentPayload {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    firmName: form.firmName.trim(),
    title: form.title.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    phoneType: form.phoneType,
  };
}

export function agentSectionIsDirty(
  agent: ListingAgentDto | null,
  form: ListingAgentSectionForm,
): boolean {
  const original = agentSectionForm(agent);
  return (Object.keys(form) as (keyof ListingAgentSectionForm)[]).some(
    (k) => form[k].trim() !== original[k].trim(),
  );
}

function setField(
  setForm: React.Dispatch<React.SetStateAction<ListingAgentSectionForm>>,
  key: keyof ListingAgentSectionForm,
  next: string,
) {
  setForm((f) => ({ ...f, [key]: next }));
}
